import { useState } from 'react';
import type { FormEvent } from 'react';

import { cadastrarParticipante } from '../api/participantes';
import type { TurmaDaOpcao } from '../api/participantes';
import { MENSAGEM_MATRICULA_INVALIDA, matriculaValida, normalizarMatricula, TETO_MATRICULA } from '../matricula';
import { ErroRespostaHttp } from '../../../shared/api/http';
import { CodigoDeAcesso, JanelaModal } from './JanelaModal';

interface CadastroDeParticipanteProps {
    turmas: TurmaDaOpcao[];
    /** A turma do filtro, quando há uma; senão, a primeira ativa (`DP-PT-023`). */
    turmaInicial: string;
    csrfToken: string;
    aoCadastrar: () => void;
    aoFechar: () => void;
}

/** Cadastro individual (`PT6`): Turma, Matrícula, Nome e Grupo opcional; o código aparece uma vez. */
export function CadastroDeParticipante({ turmas, turmaInicial, csrfToken, aoCadastrar, aoFechar }: CadastroDeParticipanteProps) {
    const [turmaId, setTurmaId] = useState(turmaInicial || turmas[0]?.id || '');
    const [matricula, setMatricula] = useState('');
    const [nome, setNome] = useState('');
    const [grupoId, setGrupoId] = useState('');
    const [erro, setErro] = useState<string | null>(null);
    const [processando, setProcessando] = useState(false);
    const [codigo, setCodigo] = useState<string | null>(null);

    const grupos = turmas.find((turma) => turma.id === turmaId)?.grupos ?? [];

    async function enviar(evento: FormEvent) {
        evento.preventDefault();
        if (processando) return;
        if (!turmaId) return setErro('Escolha a turma.');
        if (!matriculaValida(matricula)) return setErro(MENSAGEM_MATRICULA_INVALIDA);
        if (!nome.trim()) return setErro('Informe o nome.');
        setErro(null);
        setProcessando(true);
        try {
            const criado = await cadastrarParticipante({
                turmaId, matricula: normalizarMatricula(matricula), nome: nome.trim(), grupoId: grupoId || null, csrfToken,
            });
            setCodigo(criado.codigo);
            aoCadastrar();
        } catch (falha) {
            const repetida = falha instanceof ErroRespostaHttp && falha.corpo?.codigo === 'matricula_ja_cadastrada';
            setErro(repetida ? 'Matrícula já cadastrada.' : 'Não foi possível cadastrar o participante.');
        } finally {
            setProcessando(false);
        }
    }

    return (
        <JanelaModal titulo="Adicionar participante" aoFechar={aoFechar} bloqueada={processando}>
            {codigo ? (
                <CodigoDeAcesso codigo={codigo} aoConcluir={aoFechar} />
            ) : (
                <form className="formulario-participante" onSubmit={(evento) => void enviar(evento)} noValidate>
                    <label>
                        Turma
                        <select
                            value={turmaId}
                            onChange={(evento) => {
                                setTurmaId(evento.target.value);
                                setGrupoId('');
                            }}
                        >
                            {turmas.map((turma) => (
                                <option key={turma.id} value={turma.id}>{turma.nome ? `${turma.id} · ${turma.nome}` : turma.id}</option>
                            ))}
                        </select>
                    </label>
                    <label>
                        Matrícula
                        <input
                            type="text"
                            autoCapitalize="characters"
                            autoComplete="off"
                            spellCheck={false}
                            maxLength={TETO_MATRICULA + 2}
                            placeholder="Ex.: A12345"
                            value={matricula}
                            onChange={(evento) => setMatricula(evento.target.value)}
                        />
                    </label>
                    <label>
                        Nome
                        <input type="text" maxLength={120} value={nome} onChange={(evento) => setNome(evento.target.value)} />
                    </label>
                    <label>
                        Grupo
                        <select value={grupoId} onChange={(evento) => setGrupoId(evento.target.value)}>
                            <option value="">Sem grupo</option>
                            {grupos.map((grupo) => <option key={grupo.id} value={grupo.id}>{grupo.nome ?? grupo.id}</option>)}
                        </select>
                    </label>
                    {erro && <p className="erro-formulario" role="alert">{erro}</p>}
                    <div className="botoes-confirmacao">
                        <button type="button" className="botao-secundario" onClick={aoFechar} disabled={processando}>Cancelar</button>
                        <button type="submit" className="botao-confirmar botao-reativar" disabled={processando}>
                            {processando ? 'Processando…' : 'Adicionar'}
                        </button>
                    </div>
                </form>
            )}
        </JanelaModal>
    );
}
